import type { ITSPayloadBet, ITSPayloadBoard } from '../../@types/ITSPayload.js';
import BoardScore, { type ITSScoreBoard } from '../board/BoardScore.js';
import Bet from './Bet.js';

type ITSScoreBet =
  | ITSPayloadBet<{
      expertenBoardData: ITSPayloadBoard<{
        eventResults: string[][][];
      }>;
    }>
  | ITSPayloadBet<{
      scoreBoards: ITSScoreBoard[];
    }>;

type ITSScoreERBet = Omit<
  ITSPayloadBet<{
    erScoreBoardData: {
      boards: ITSPayloadBoard<string>[];
      numMatchesPerBoard: number | undefined;
    };
  }>,
  'erScoreBoardData'
>;

/**
 * Bet class for Score products like Bomben, Matchen and Fullträff.
 * Contains product specific logic for eg. reduction systems like RSYS.
 */
class BetScore extends Bet<BoardScore> {
  getSystemLabel(): string {
    const systemType = this.boards[0]?.systemType;
    if (systemType === 'RSYS') {
      return `R ${this.getRowCount()}`;
    }
    if (systemType === 'MSYS') {
      return `M ${this.getRowCount()}`;
    }

    return `S ${this.getRowCount()}`;
  }

  /**
   * Returns the total amount of boards if the bet has the XPERTEN bet property
   * Otherwise it returns the total amount of rows for all boards.
   *
   * @returns Rows
   */
  override getRowCount(): number {
    if (this.betProperties.includes('XPERTEN')) {
      return this.boards.length;
    }

    return super.getRowCount();
  }

  /**
   * Generates a XPERTEN board data of all SROW.
   * Will reduce the duplicates of outcome combinations for each event since that's what ITS expects.
   *
   * Handle XPERTEN board as a special case to maintain the internal structure of combinations (mathematical) and not as
   * outcome pairs that XPERTEN would generate for us.
   *
   * Eg. these outcome pairs for three events will generate 24 single rows as 3 * 2 * 4 = 24
   * ["1", "3"], ["2", "0"], ["4", "2"] => 3 rows
   * ["0", "2"], ["4", "2"] => 2 rows
   * ["1", "0"], ["1", "1"], ["1", "2"], ["4", "2"] => 4 rows.
   *
   * @returns ITS board data for XPERTEN
   */
  protected composeXperten(): ITSPayloadBoard<{
    eventResults: string[][][];
  }> {
    const eventResults: string[][][] = [];
    for (const board of this.boards) {
      for (const [eventIndex, outcomeGroup] of Object.entries(board.outcomes)) {
        const eventIndexAsNumber = Number.parseInt(eventIndex, 10);
        const eventPairs = eventResults[eventIndexAsNumber] ?? ([] as string[][]);
        const outcomePairStr = outcomeGroup.join(',');
        const isNotUnique = eventPairs.some((pair) => pair.join(',') === outcomePairStr);
        eventResults[eventIndexAsNumber] = [
          ...eventPairs,
          ...(isNotUnique ? [] : [outcomeGroup.map((outcome) => outcome.toString())]),
        ];
      }
    }

    const itsXpertenBoard = {
      eventResults,
    };

    type ITSXpertenBoard = ITSPayloadBoard<typeof itsXpertenBoard>;
    return itsXpertenBoard as ITSXpertenBoard;
  }

  /**
   * Loops over boards to generate correct data for all boards.
   *
   * @returns Object with correct ITS structure for score external systems
   */
  protected composeOwnRowsBoards(): ITSScoreERBet {
    const boards = this.boards.map((board) => board.composeOwnRows());
    const numMatchesPerBoard = this.boards[0]?.outcomes.length;

    const itsPayload = {
      erScoreBoardData: {
        boards,
        numMatchesPerBoard,
      },
    };

    type ITSBetScore = ITSPayloadBet<typeof itsPayload>;
    // Make numMatchesPerBoard optional
    type ITSBetScoreOptional = Omit<ITSBetScore, 'erScoreBoardData'> & {
      erScoreBoardData: Omit<ITSBetScore['erScoreBoardData'], 'numMatchesPerBoard'> &
        Partial<Pick<ITSBetScore['erScoreBoardData'], 'numMatchesPerBoard'>>;
    };

    return itsPayload as ITSBetScoreOptional;
  }

  protected composeBoards(): ITSScoreBet {
    if (
      this.betProperties.includes('XPERTEN') &&
      // Handle Bomben and Matchen explicity since Fullträff is a score product
      ['Bomben', 'Matchen'].includes(this.product)
    ) {
      const expertenBoardData = this.composeXperten();
      const itsPayload = {
        expertenBoardData,
      };

      type ITSBetScoreQP = ITSPayloadBet<typeof itsPayload>;

      return itsPayload as ITSBetScoreQP;
    }

    const scoreBoards = this.boards.map((board) => board.compose());

    const itsPayload = { scoreBoards };

    type ITSBetScore = ITSPayloadBet<typeof itsPayload>;

    return itsPayload as ITSBetScore;
  }

  protected getBoard(): BoardScore {
    return new BoardScore(this.product);
  }
}

export default BetScore;
export type BetScoreCompose = ReturnType<BetScore['composeBoards']>;
